import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ElementReport, IssueReport, ReportNote } from '../types/admin';
import { MessageSquare, Clock, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { updateReportStatus } from '../lib/admin-api';
import { useToast } from '@/hooks/use-sonner-toast';
import { useAdmin } from '../contexts/AdminContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Report = ElementReport | IssueReport;

interface ReportListProps {
  reports: Report[];
  onRefresh: () => Promise<Report[]>;
}

const ReportList: React.FC<ReportListProps> = ({ reports, onRefresh }) => {
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [reportNote, setReportNote] = useState<string>('');
  const [showDismissed, setShowDismissed] = useState<boolean>(false);
  const { toast } = useToast();
  const { adminUser } = useAdmin();

  // Helper function to handle both Firebase Timestamp and regular Date objects
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';

    let date: Date;

    // Handle Firebase Timestamp objects (which have toDate method)
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    }
    // Handle Date objects
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle string or number timestamps
    else {
      date = new Date(timestamp);
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isElementReport = (report: Report): report is ElementReport => {
    return 'elementId' in report;
  };

  const handleViewNotes = (report: Report) => {
    setSelectedReport(report);
    setIsDialogOpen(true);
  };

  const handleChangeStatus = async (report: Report, status: string) => {
    if (!adminUser) return;

    try {
      // Show a subtle toast to indicate the update is in progress
      const toastId = toast({
        title: 'Updating...',
        description: `Changing status to ${status}`,
      });

      await updateReportStatus(
        report.id,
        status,
        '', // No note for status changes
        adminUser.uid,
        adminUser.displayName
      );

      // Update the toast to show success
      toast({
        id: toastId,
        title: 'Status Updated',
        description: `Report status changed to ${status}`,
        variant: 'success',
      });

      // Refresh the data
      onRefresh();
    } catch (error) {
      console.error('Error updating report status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update report status',
        variant: 'destructive',
      });
    }
  };

  const handleAddNote = async () => {
    if (!selectedReport || !adminUser || !reportNote.trim()) return;

    try {
      // Show a subtle toast to indicate the update is in progress
      const toastId = toast({
        title: 'Updating...',
        description: 'Adding note to report',
      });

      await updateReportStatus(
        selectedReport.id,
        selectedReport.status || 'in-progress',
        reportNote,
        adminUser.uid,
        adminUser.displayName
      );

      // Clear the note input
      setReportNote('');

      // Update the toast to show success
      toast({
        id: toastId,
        title: 'Success',
        description: 'Note added successfully',
        variant: 'success',
      });

      // Refresh the data and update the selected report
      const updatedReports = await onRefresh();
      const updatedReport = updatedReports?.find((r) => r.id === selectedReport.id);
      if (updatedReport) {
        setSelectedReport(updatedReport);
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Error',
        description: 'Failed to add note',
        variant: 'destructive',
      });
    }
  };

  // Group reports by status
  const groupedReports = reports.reduce((acc, report) => {
    const status = report.status || 'new';
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(report);
    return acc;
  }, {} as Record<string, Report[]>);

  // Sort reports within each group by timestamp (newest first)
  Object.keys(groupedReports).forEach((status) => {
    groupedReports[status].sort((a, b) => {
      // Convert timestamps to Date objects using our helper function
      const dateAStr = formatDate(a.timestamp);
      const dateBStr = formatDate(b.timestamp);

      // Parse the formatted dates back to Date objects for comparison
      const dateA = new Date(dateAStr);
      const dateB = new Date(dateBStr);

      return dateB.getTime() - dateA.getTime();
    });
  });

  // Define the order of status sections
  const statusOrder = ['new', 'in-progress', 'resolved', 'dismissed'];

  // Function to render a report card
  const renderReportCard = (report: Report) => (
    <Card key={report.id} className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Left side - Report info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-purple-600 hover:bg-purple-700">
                {isElementReport(report) ? 'Element' : 'Issue'}
              </Badge>
              <Badge
                className={
                  report.status === 'new'
                    ? 'bg-blue-500 hover:bg-blue-600'
                    : report.status === 'in-progress'
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : report.status === 'resolved'
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-gray-700 hover:bg-gray-800'
                }
              >
                {report.status?.charAt(0).toUpperCase() + report.status?.slice(1) || 'New'}
              </Badge>
              <Badge
                variant="outline"
                className={`cursor-pointer ${
                  Array.isArray(report.notes) &&
                  report.notes.filter((note) => !note.text?.startsWith('Status changed to'))
                    .length > 0
                    ? 'bg-purple-50 dark:bg-purple-950/30'
                    : ''
                }`}
                onClick={() => handleViewNotes(report)}
              >
                <MessageSquare className="h-3 w-3 mr-1" />
                {Array.isArray(report.notes)
                  ? report.notes.filter((note) => !note.text?.startsWith('Status changed to'))
                      .length
                  : 0}
              </Badge>
            </div>
            <div className="mb-2">
              <h3 className="font-medium">
                {isElementReport(report) ? (report as ElementReport).elementName : 'Issue Report'}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {report.description || 'No description provided'}
              </p>
            </div>
            <div className="flex items-center text-xs text-muted-foreground">
              <User className="h-3 w-3 mr-1" />
              <span className="mr-3">{report.userEmail}</span>
              <Clock className="h-3 w-3 mr-1" />
              <span>{formatDate(report.timestamp)}</span>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2 mt-3 md:mt-0">
            <Select
              value={report.status || 'new'}
              onValueChange={(value) => handleChangeStatus(report, value)}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Change status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewNotes(report)}
              className="flex-1 md:flex-none"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Notes
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8">
      {reports.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No reports found</p>
        </div>
      ) : (
        <>
          {statusOrder
            .filter((status) => status !== 'dismissed' || showDismissed)
            .map((status) => {
              const statusReports = groupedReports[status] || [];

              return (
                <div key={status} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">
                      {status === 'new'
                        ? 'New'
                        : status === 'in-progress'
                        ? 'In Progress'
                        : status === 'resolved'
                        ? 'Resolved'
                        : 'Dismissed'}
                    </h2>
                    <div className="text-sm text-muted-foreground">
                      {statusReports.length} report{statusReports.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {statusReports.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Empty</p>
                      </div>
                    ) : (
                      statusReports.map((report) => renderReportCard(report))
                    )}
                  </div>

                  {status !== statusOrder[statusOrder.length - 1] && (
                    <div className="border-t border-gray-200 dark:border-gray-800 my-6"></div>
                  )}
                </div>
              );
            })}

          <div className="flex justify-center mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDismissed(!showDismissed)}
              className="text-sm"
            >
              {showDismissed ? 'Hide Dismissed Reports' : 'Show Dismissed Reports'}
            </Button>
          </div>
        </>
      )}

      {/* Notes Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Report Notes</DialogTitle>
            <DialogDescription>
              {selectedReport && (
                <div className="flex flex-wrap gap-2 mt-1">
                  <Badge className="bg-purple-600 hover:bg-purple-700">
                    {isElementReport(selectedReport) ? 'Element' : 'Issue'}
                  </Badge>
                  <Badge
                    className={
                      selectedReport.status === 'new'
                        ? 'bg-blue-500 hover:bg-blue-600'
                        : selectedReport.status === 'in-progress'
                        ? 'bg-orange-500 hover:bg-orange-600'
                        : selectedReport.status === 'resolved'
                        ? 'bg-green-500 hover:bg-green-600'
                        : 'bg-gray-700 hover:bg-gray-800'
                    }
                  >
                    {selectedReport.status?.charAt(0).toUpperCase() +
                      selectedReport.status?.slice(1) || 'New'}
                  </Badge>
                  {isElementReport(selectedReport) && (
                    <span className="text-sm">{(selectedReport as ElementReport).elementName}</span>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4 py-4">
              {/* Add Note Form */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (reportNote.trim()) {
                    handleAddNote();
                  }
                }}
              >
                <Label htmlFor="note" className="text-sm font-medium mb-2 block">
                  Add Note
                </Label>
                <Textarea
                  id="note"
                  value={reportNote}
                  onChange={(e) => setReportNote(e.target.value)}
                  placeholder="Add a note..."
                  className="min-h-[80px] resize-none"
                />
                <div className="flex justify-end mt-2">
                  <Button type="submit" size="sm" disabled={!reportNote.trim()}>
                    Add Note
                  </Button>
                </div>
              </form>

              {/* Notes List */}
              <div className="mt-6">
                <Label className="text-sm font-medium mb-2 block">Notes</Label>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 border rounded-md p-3">
                  {Array.isArray(selectedReport.notes) && selectedReport.notes.length > 0 ? (
                    // Filter out status change notes and only show manual notes
                    selectedReport.notes
                      .filter((note) => !note.text?.startsWith('Status changed to'))
                      .map((note: ReportNote, index: number) => (
                        <div
                          key={index}
                          className="p-3 rounded-md border-l-2 border-purple-500 bg-purple-50 dark:bg-purple-950/30"
                        >
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span className="font-medium">{note.adminName || 'Admin'}</span>
                            <span className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {formatDate(note.timestamp)}
                            </span>
                          </div>
                          <div>{note.text}</div>
                        </div>
                      ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No notes to display
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportList;
